'use client';

import { AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { useEffect } from 'react';

import { useSignOut } from '../../hooks/auth';
import { logRed } from '../../utils/debugger';
import NetworkAxiosError from '../../utils/helpers/network-axios-error.ts';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion.tsx';
import { Button } from '../ui/button.tsx';
import { CardContent } from '../ui/card.tsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { ScrollArea, ScrollBar } from '../ui/scroll-area.tsx';
import { Text } from '../ui/typography';

export const RobotIllustration = ({ size = 120 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#fff"
      fillRule="evenodd"
      d="M14 2a2 2 0 0 1-1 1.73V4h7a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h7v-.27A2 2 0 1 1 14 2zM4 6h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z"
      clipRule="evenodd"
    />
    <path
      fill="#fff"
      d="M5.63 7.74a1.05 1.05 0 0 0-.73.33 1.05 1.05 0 0 0 .05 1.49l1.46 1.36-1.46 1.37a1.05 1.05 0 0 0-.05 1.48 1.05 1.05 0 0 0 1.49.05l1.56-1.46 1.57 1.46a1.05 1.05 0 0 0 1.48-.05 1.05 1.05 0 0 0-.05-1.48l-1.46-1.37 1.46-1.36A1.05 1.05 0 0 0 11 8.07a1.05 1.05 0 0 0-1.48-.05L7.95 9.5 6.4 8.02a1.05 1.05 0 0 0-.76-.28Zm8.17 0a1.05 1.05 0 0 0-.74.33 1.05 1.05 0 0 0 .05 1.49l1.46 1.36-1.46 1.37a1.05 1.05 0 0 0-.05 1.48 1.05 1.05 0 0 0 1.49.05l1.56-1.46 1.56 1.46a1.05 1.05 0 0 0 1.49-.05 1.05 1.05 0 0 0-.05-1.48l-1.46-1.37 1.46-1.36a1.05 1.05 0 0 0 .05-1.49 1.05 1.05 0 0 0-1.49-.05L16.11 9.5l-1.56-1.47a1.05 1.05 0 0 0-.76-.28ZM12 15.92a3.4 3.4 0 0 0-2.1.7 2.28 2.28 0 0 0-.98 1.8 1.05 1.05 0 0 0 1.05 1.06 1.05 1.05 0 0 0 1.05-1.05c0 .12 0-.02.17-.15.17-.14.46-.26.81-.26.41 0 .74.11.9.22.16.11.15.14.15.19a1.05 1.05 0 0 0 1.05 1.05 1.05 1.05 0 0 0 1.05-1.05c0-.8-.47-1.5-1.06-1.92-.6-.4-1.32-.6-2.09-.6z"
      paintOrder="markers stroke fill"
    />
  </svg>
);

type Props = {
  error: (Error & { digest?: string }) | NetworkAxiosError;
  reset: () => void;
};

function ErrorBox({ error, reset }: Props) {
  const { loadingLogout, handleLogout } = useSignOut();
  useEffect(() => {
    logRed('Error', { error });
  }, [error]);

  // @ts-expect-error - todo do toJson correctly, new version of TS have tooling for this
  const showLogout = error?.status === 401;

  const message = error?.message || 'An unknown error has occurred.';
  // @ts-expect-error - todo do toJson correctly, new version of TS have tooling for this
  const body = (error.toJson && error.toJson()) || error.stack || error.toString();
  return (
    <div className="w-full h-full flex justify-center items-center p-10">
      <Dialog open={true}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <span className="text-red-700 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Oops! An error occurred
              </span>
            </DialogTitle>
            {/*<DialogDescription>{message}</DialogDescription>*/}
          </DialogHeader>
          <div className="w-full">
            <CardContent className="flex flex-col gap-2 justify-center items-center text-center p-5">
              <RobotIllustration />
            </CardContent>
            <Accordion type="single" collapsible>
              <AccordionItem value="error">
                <AccordionTrigger className="text-left border-2 py-2 px-3">
                  {message}
                </AccordionTrigger>
                <AccordionContent className="bg-background overflow-auto border-2 border-t-0">
                  <ScrollArea className="h-48 max-w-md m-2">
                    <pre>
                      <code className="text-sm text-muted-foreground">{body}</code>
                    </pre>
                    <ScrollBar orientation="vertical" />
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <DialogFooter>
            <div className="flex flex-1 justify-between">
              <Text variant="description">Return to the Home page or try again.</Text>
              <Button variant="outline" onClick={reset}>
                Try again
              </Button>

              <div className="space-x-2">
                {showLogout ? (
                  <Button loading={loadingLogout} onClick={handleLogout}>
                    Sign In again
                  </Button>
                ) : (
                  <Link href="/home">
                    <Button variant="default">Home</Button>
                  </Link>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { ErrorBox };
